import { useTranslation } from "react-i18next";
import type { Webhook } from "~/api/governanceTypes";
import { useWebhookDeliveries } from "~/api/queries";
import { Pill } from "~/features/automation/components";
import { Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { formatRelativeTime } from "~/foundation/utils/time";

export function WebhookDeliveriesDialog({
  webhook,
  onClose,
}: {
  webhook: Webhook;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: deliveries, isLoading } = useWebhookDeliveries(webhook.id);

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Deliveries")}
      description={webhook.url}
      closeLabel={t("Close")}
      size="xl"
    >
      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : !deliveries?.length ? (
        <Empty message={t("No deliveries yet")} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-start text-small">
            <thead>
              <tr className="border-b border-divider text-text-tertiary">
                <th className="px-2 py-1.5 text-start font-medium">{t("Event")}</th>
                <th className="px-2 py-1.5 text-start font-medium">{t("Status")}</th>
                <th className="px-2 py-1.5 text-start font-medium">{t("Attempt")}</th>
                <th className="px-2 py-1.5 text-start font-medium">{t("HTTP")}</th>
                <th className="px-2 py-1.5 text-start font-medium">{t("When")}</th>
                <th className="px-2 py-1.5 text-start font-medium">{t("Error")}</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((delivery) => (
                <tr key={delivery.id} className="border-b border-divider last:border-b-0">
                  <td className="px-2 py-1.5 font-mono">{delivery.eventType}</td>
                  <td className="px-2 py-1.5">
                    <Pill
                      tone={
                        delivery.status === "pending"
                          ? "warning"
                          : delivery.status === "success"
                            ? "success"
                            : "danger"
                      }
                    >
                      {delivery.status}
                    </Pill>
                  </td>
                  <td className="px-2 py-1.5 tabular-nums">{delivery.attempt}</td>
                  <td className="px-2 py-1.5 tabular-nums">{delivery.statusCode ?? "—"}</td>
                  <td className="px-2 py-1.5 text-text-tertiary">
                    {formatRelativeTime(delivery.createdAt, i18n.language)}
                  </td>
                  <td className="px-2 py-1.5 text-danger">{delivery.error ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}
